import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, SpecraActionList, SpecraEyebrow, SpecraText, specraFocusRing } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowRight, BarChart3, Calendar, ChevronDown, ChevronRight, ClipboardCheck, DollarSign, FileQuestion, SendHorizontal, Stamp } from 'lucide-react';
import { useId, useState } from 'react';
import { useProgramFinancialSummary } from '../../../../components/delivery/widgets/useProgramFinancialSummary';
import { financialSectionVisible, programRef } from '../../../../lib/access/types';
import { useView } from '../../../../lib/access/useView';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { OccurrenceWindow, useOccurrenceDecisions, useOccurrences } from '../../Deliverables/hooks';
import { addDays, todayISO } from '../../Deliverables/lib/format';
import { lateEventRows } from '../../Deliverables/lib/rule';
import { useProgramModificationChangeSets } from '../v2/modificationHooks';
/** The last calendar day of `iso`'s month ("2026-09-30" for any September day). */ function monthEnd(iso) {
    const [y, m] = iso.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
    return addDays(next, -1);
}
function sumStates(counts, states) {
    return states.reduce((n, s)=>n + (counts?.[s] ?? 0), 0);
}
/**
 * The deliverable signals, from the contract model (D112 #6, #8): late over
 * the program's KPI window (the contract period containing today, plus open
 * dates carried over — D104), still owed and due between today and month end,
 * and proposals awaiting approval. Every count is pangaea's `state_counts`;
 * page_size 1 asks for the counts without the rows.
 */ function useDeliverableSignals(programId) {
    const today = todayISO();
    const period = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        pageSize: 1
    });
    const month = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        dueFrom: today,
        dueTo: monthEnd(today),
        pageSize: 1
    });
    const decisions = useOccurrenceDecisions(programId, {
        openOnly: true
    });
    return {
        isLoading: period.isLoading || month.isLoading || decisions.isLoading,
        late: period.isError ? null : period.data?.stateCounts.late ?? 0,
        dueThisMonth: month.isError ? null : sumStates(month.data?.stateCounts, [
            'due_soon',
            'upcoming'
        ]),
        awaiting: decisions.isError ? null : decisions.data?.decisions ?? []
    };
}
/**
 * The program-home Do Next queue (LAYOUT-SPEC §2A): 3–6 ranked actions, each
 * with a verb and a destination. Derived from the same real signals the KPIs
 * read (overdue/ due deliverables, audit state, missing scores/financials) —
 * no queue table, no new RPC. Empty when the program is genuinely on track.
 */ export function DoNextQueue({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const [expanded, setExpanded] = useState(false);
    const listId = useId();
    const signals = useDeliverableSignals(programId);
    const summary = useProgramFinancialSummary(programId);
    const revealFinancials = financialSectionVisible(useView(programRef(programId)).fieldMasked);
    // Review items (D93) the uploads couldn't settle; the program's change sets
    // hold nothing applied until each is decided.
    const changeSets = useProgramModificationChangeSets(programId, useTenantId());
    const openReview = (changeSets.data ?? []).reduce((n, cs)=>n + cs.openReviewCount, 0);
    // SAD-834: the late rows themselves, to tell a late event from a late
    // deliverable. The KPI count stays pangaea's.
    const lateRows = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        states: [
            'late',
            'scheduled'
        ],
        pageSize: 200
    });
    const lateEvents = lateEventRows(lateRows.data?.occurrences ?? []);
    const loading = signals.isLoading || summary.isLoading;
    const kpi = summary.data?.kpiSnapshot;
    const items = [];
    // First: until these are decided, the contract changes behind them stay
    // unapplied. A roster that failed to load is never read as zero: the row
    // says the count is unknown and still points at the review.
    if (changeSets.isError && !changeSets.data) {
        items.push({
            id: 'review-items-unknown',
            label: 'Check the contract changes for facts that need your call',
            meta: "Couldn't count",
            href: `${base}/docs?tab=modifications`,
            icon: /*#__PURE__*/ _jsx(FileQuestion, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (openReview > 0) {
        items.push({
            id: 'review-items',
            label: "Decide the contract facts the documents didn't settle",
            meta: `${openReview} need${openReview === 1 ? 's' : ''} your call`,
            href: `${base}/docs?tab=modifications`,
            icon: /*#__PURE__*/ _jsx(FileQuestion, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    }
    // A count that failed to load is never read as zero: the row says so and
    // still points at the list.
    if (signals.late === null) {
        items.push({
            id: 'overdue-unknown',
            label: 'Check late deliverables',
            meta: "Couldn't count",
            href: `${base}/deliverables?state=late`,
            icon: /*#__PURE__*/ _jsx(SendHorizontal, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (signals.late - lateEvents > 0) {
        items.push({
            id: 'overdue',
            label: 'Submit late deliverables — attach the files and delivery emails',
            meta: `${signals.late - lateEvents} late`,
            href: `${base}/deliverables?state=late`,
            icon: /*#__PURE__*/ _jsx(SendHorizontal, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'danger'
        });
    }
    // D112 #8: proposals wait on a second person; the row opens the
    // awaiting-approval list, where they can be approved together.
    const awaiting = signals.awaiting;
    if (awaiting === null) {
        items.push({
            id: 'awaiting-approval-unknown',
            label: 'Check proposals awaiting approval',
            meta: "Couldn't count",
            href: `${base}/deliverables?awaiting=1`,
            icon: /*#__PURE__*/ _jsx(Stamp, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if (awaiting.length > 0) {
        items.push({
            id: 'awaiting-approval',
            label: `Awaiting approval (${awaiting.length})`,
            meta: 'Proposals a second person must approve',
            href: `${base}/deliverables?awaiting=1`,
            icon: /*#__PURE__*/ _jsx(Stamp, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    const hasAudit = !!kpi?.hasAudit && (kpi?.latestAuditTotal ?? 0) > 0;
    if (!hasAudit) {
        items.push({
            id: 'audit',
            label: 'Run the SDS audit to establish a compliance score',
            href: `${base}/audits`,
            icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    } else if ((kpi?.latestAuditPass ?? 0) < (kpi?.latestAuditTotal ?? 0)) {
        const gap = (kpi?.latestAuditTotal ?? 0) - (kpi?.latestAuditPass ?? 0);
        items.push({
            id: 'audit-gap',
            label: 'Resolve failing audit checks — each becomes a correction',
            meta: `${gap} failing`,
            href: `${base}/audits`,
            icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'warning'
        });
    }
    if (kpi && !kpi.hasCpars && !kpi.hasQasp) {
        items.push({
            id: 'scores',
            label: 'Enter customer scores — CPARS and QASP',
            href: `${base}/quality`,
            icon: /*#__PURE__*/ _jsx(BarChart3, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    const noCurrentMonth = !summary.data?.financialHealth?.hasCurrent;
    if (revealFinancials && noCurrentMonth) {
        items.push({
            id: 'financials',
            label: "Record this month's financials",
            href: `${base}/financial`,
            icon: /*#__PURE__*/ _jsx(DollarSign, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'info'
        });
    }
    if (signals.dueThisMonth !== null && signals.dueThisMonth > 0) {
        items.push({
            id: 'due',
            label: 'Deliverables due this month — plan submissions',
            meta: `${signals.dueThisMonth} due`,
            href: `${base}/deliverables`,
            icon: /*#__PURE__*/ _jsx(Calendar, {
                className: "h-3.5 w-3.5"
            }),
            tone: 'neutral'
        });
    }
    const shown = items.slice(0, 6);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.programHome.doNextContainer,
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                className: `-mx-2 box-content flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent/50 ${specraFocusRing}`,
                "aria-expanded": expanded,
                "aria-controls": listId,
                onClick: ()=>setExpanded((e)=>!e),
                "data-testid": Testids.programHome.doNextToggle,
                children: [
                    expanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                        className: "size-4 shrink-0",
                        "aria-hidden": "true"
                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                        className: "size-4 shrink-0",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: loading ? 'Do Next' : `Do Next (${shown.length})`
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "block truncate",
                                size: "caption",
                                tone: "muted",
                                children: "Suggested next steps for this program."
                            })
                        ]
                    })
                ]
            }),
            expanded ? /*#__PURE__*/ _jsx("div", {
                id: listId,
                children: loading ? /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-24 w-full"
                }) : items.length === 0 && changeSets.isLoading ? // Not "nothing needs attention" until the review count is in.
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-3",
                    "data-testid": Testids.programHome.doNextPending,
                    children: /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-10 w-full"
                    })
                }) : items.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "body-ui",
                    tone: "muted",
                    className: "mt-3",
                    children: "Nothing needs attention — deliverables are on track, the audit is passing, and scores are recorded."
                }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                    className: "mt-1",
                    items: shown,
                    renderLink: renderRouterLink,
                    getItemTestId: ()=>Testids.programHome.doNextRow,
                    getItemProps: (item)=>({
                            'data-tone': item.tone
                        }),
                    trailing: /*#__PURE__*/ _jsx(ArrowRight, {
                        className: "h-3 w-3"
                    })
                })
            }) : null
        ]
    });
}
